import React, { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Mail, ArrowLeft, CheckCircle, AlertCircle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/auth/forgot-password`, { email });
      setSent(true);
    } catch (err) {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="min-h-screen bg-[#020617] flex items-center justify-center px-4">
        <div className="w-full max-w-md">
          <div className="bg-slate-900/80 border border-slate-800 !rounded-[10px] p-8 text-center">
            <div className="w-14 h-14 !rounded-[10px] bg-emerald-400/10 flex items-center justify-center mx-auto mb-5">
              <CheckCircle size={28} className="text-emerald-400" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2" data-testid="reset-sent-title">Check your email</h1>
            <p className="text-slate-400 text-sm mb-6">
              If <strong className="text-white">{email}</strong> is registered, you'll receive a password reset link shortly. The link expires in 15 minutes.
            </p>
            <Link
              to="/admin"
              className="inline-flex items-center gap-2 text-amber-400 hover:text-amber-300 text-sm transition-colors"
              data-testid="back-to-login-link"
            >
              <ArrowLeft size={16} />
              Back to login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#020617] flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <Link
          to="/admin"
          className="inline-flex items-center gap-2 text-slate-400 hover:text-amber-400 transition-colors mb-8 text-sm"
          data-testid="back-to-login-link"
        >
          <ArrowLeft size={16} />
          Back to login
        </Link>

        <div className="bg-slate-900/80 border border-slate-800 !rounded-[10px] p-8">
          <div className="flex items-center justify-center mb-6">
            <img
              src="https://customer-assets.emergentagent.com/job_project-flow-beta/artifacts/dh5z86or_PM%20Flow.svg"
              alt="PM Flow Logo"
              className="h-10 w-auto"
            />
          </div>

          <h1 className="text-2xl font-bold text-white text-center mb-2" data-testid="forgot-password-title">
            Forgot Password
          </h1>
          <p className="text-slate-400 text-center text-sm mb-8">
            Enter your email and we'll send you a reset link.
          </p>

          {error && (
            <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/30 !rounded-[10px] p-3 mb-6 text-red-400 text-sm" data-testid="forgot-error">
              <AlertCircle size={16} className="shrink-0" />
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm text-slate-300 mb-2">Email</label>
              <div className="relative">
                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@email.com"
                  required
                  className="pl-10 bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 !rounded-[10px]"
                  data-testid="forgot-email-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-amber-400 hover:bg-amber-300 text-slate-900 font-semibold !rounded-[10px] h-11"
              data-testid="forgot-submit-button"
            >
              {loading ? "Sending..." : "Send Reset Link"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
